import { ALL_ROLES, ROLE_LABELS, ROLES } from "../config/roles";

const PENDING_USER_KEY = "simcollect.ops.pendingUser";

/** Resolve API role name/slug into a stable ops role slug for RBAC + labels. */
function resolveOpsRoleSlug(fields) {
  const slug = fields?.role_slug;
  if (slug && ALL_ROLES.includes(slug)) return slug;
  if (fields?.role && ALL_ROLES.includes(fields.role)) return fields.role;

  const label = String(fields?.role ?? "").trim().toLowerCase();
  if (label) {
    const matched = Object.entries(ROLE_LABELS).find(
      ([, display]) => String(display).toLowerCase() === label
    );
    if (matched) return matched[0];
  }

  return slug ?? fields?.role ?? null;
}

/** Map backend boolean / string status → display string for badges. */
export function formatAccountStatus(status) {
  if (status === true || status === 1 || status === "active") return "active";
  if (status === false || status === 0 || status === "inactive") return "inactive";
  if (typeof status === "string") return status.toLowerCase();
  return "active";
}

/** Normalize phone for display (backend may omit leading +). */
export function normalizePhoneNumber(phone) {
  if (!phone) return "";
  const value = String(phone).trim();
  if (value.startsWith("+")) return value;
  if (/^\d+$/.test(value)) return `+${value}`;
  return value;
}

/**
 * Display name from backend first/last name fields (Admin has no `name` column).
 */
export function getOpsUserDisplayName(user) {
  if (!user || typeof user !== "object") return "Ops user";
  const composed = [user.first_name, user.last_name].filter(Boolean).join(" ").trim();
  if (composed) return composed;
  if (user.name) return String(user.name).trim();
  if (user.email) return String(user.email).split("@")[0];
  return "Ops user";
}

/**
 * Map Laravel Admin / ops auth payload → Redux user.
 * Mirrors backend `admins` columns (+ `role` / `permissions` from auth).
 * Strips token fields — those live separately on the auth slice.
 */
export function normalizeOpsUser(raw) {
  if (!raw || typeof raw !== "object") return null;

  const {
    token: _token,
    access_token: _accessToken,
    auth_token: _authToken,
    bearer_token: _bearerToken,
    token_type: _tokenType,
    expires_in: _expiresIn,
    // Legacy / non-admin keys — never store on Redux user
    email_verified_at: _emailVerifiedAt,
    remember_token: _rememberToken,
    phone: legacyPhone,
    name: legacyName,
    ...userFields
  } = raw;

  const firstName = userFields.first_name?.trim() ?? "";
  const lastName = userFields.last_name?.trim() ?? "";
  const phoneNumber =
    userFields.phone_number != null && String(userFields.phone_number).trim() !== ""
      ? String(userFields.phone_number).trim()
      : legacyPhone != null && String(legacyPhone).trim() !== ""
        ? String(legacyPhone).trim()
        : null;

  const composedName = [firstName, lastName].filter(Boolean).join(" ").trim();
  const displayName =
    composedName ||
    (legacyName ? String(legacyName).trim() : "") ||
    userFields.email?.split("@")[0] ||
    "Ops user";

  // Prefer explicit names; if API only sent a combined name, split once for Redux.
  let resolvedFirst = firstName;
  let resolvedLast = lastName;
  if (!resolvedFirst && !resolvedLast && legacyName) {
    const parts = String(legacyName).trim().split(/\s+/);
    resolvedFirst = parts[0] || "";
    resolvedLast = parts.slice(1).join(" ");
  }

  const flatPermissions = (() => {
    const rawPerms = userFields.permissions;
    if (Array.isArray(rawPerms)) {
      return rawPerms
        .map((p) => (typeof p === "string" ? p : p?.name))
        .filter((p) => typeof p === "string" && p.includes("."));
    }
    if (rawPerms && typeof rawPerms === "object") {
      return Object.values(rawPerms)
        .flat()
        .map((p) => (typeof p === "string" ? p : p?.name))
        .filter((p) => typeof p === "string" && p.includes("."));
    }
    return [];
  })();

  return {
    id: userFields.id ?? null,
    first_name: resolvedFirst,
    last_name: resolvedLast,
    email: userFields.email?.trim().toLowerCase() ?? "",
    phone_number: phoneNumber,
    last_login_at: userFields.last_login_at ?? null,
    admin_slug: userFields.admin_slug ?? null,
    role_slug: userFields.role_slug ?? null,
    status: userFields.status ?? true,
    created_at: userFields.created_at ?? null,
    deleted_at: userFields.deleted_at ?? null,
    // Auth enrichments (not DB columns, but returned by verify/login)
    role: resolveOpsRoleSlug(userFields),
    permissions: flatPermissions,
    // UI helpers derived from backend fields
    name: displayName,
    phone: normalizePhoneNumber(phoneNumber),
  };
}

export function extractTokenFromPayload(raw) {
  if (!raw || typeof raw !== "object") return null;
  return raw.token ?? raw.access_token ?? raw.auth_token ?? raw.bearer_token ?? null;
}

export function savePendingOpsUser(user) {
  if (!user) {
    sessionStorage.removeItem(PENDING_USER_KEY);
    return;
  }
  sessionStorage.setItem(PENDING_USER_KEY, JSON.stringify(user));
}

export function loadPendingOpsUser() {
  try {
    const raw = sessionStorage.getItem(PENDING_USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearPendingOpsUser() {
  sessionStorage.removeItem(PENDING_USER_KEY);
}

/** True when user has super_admin role from API. */
export function isSuperAdminUser(user) {
  return user?.role === ROLES.SUPER_ADMIN;
}
